// 숫자 카운트업 효과 — from→to 로 증가하는 정수를 표시한다(매출·통계 강조 등).
// TextEffectRender.visibleText 로 현재 숫자를 내보내 텍스트 내용을 대체한다(타자기와 같은 축).
import type {
  TextEffectConfig,
  TextEffectDef,
  TextEffectRender,
} from './types';

/**
 * 현재 프레임의 카운트업 값(정수).
 * - durationInSeconds ≤ 0 → 즉시 to
 * - frame ≤ 0 → from
 * - 그 외 → from + (to-from) × clamp(frame/totalFrames, 0..1), 반올림. ratio=1 에서 정확히 to.
 */
export function countUpValue(
  from: number,
  to: number,
  frame: number,
  fps: number,
  durationInSeconds: number,
): number {
  if (durationInSeconds <= 0) return Math.round(to);
  if (frame <= 0) return Math.round(from);
  const totalFrames = durationInSeconds * fps;
  const ratio = Math.max(0, Math.min(1, frame / totalFrames));
  return Math.round(from + (to - from) * ratio);
}

/**
 * 정수를 천단위 구분(,)해 문자열로. 로케일 의존 함수(toLocaleString)는 preview(브라우저)와
 * Lambda(node) 렌더 결과가 갈릴 수 있어 쓰지 않는다 — 결정적 정규식 그룹핑.
 */
export function formatGrouped(value: number): string {
  const negative = value < 0;
  const digits = String(Math.abs(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return negative ? `-${digits}` : digits;
}

/** 카운트업 효과 정의(레지스트리 등록 대상). */
export const countUpEffect: TextEffectDef = {
  type: 'countUp',
  // 인스펙터에서 t(label) 로 해석되는 i18n 키.
  label: 'Editor.inspector.text.effect-countup',
  defaultConfig: {
    type: 'countUp',
    from: 0,
    to: 100,
    durationInSeconds: 1.5,
  },
  compute: (
    fullText: string,
    frame: number,
    fps: number,
    config: TextEffectConfig,
  ): TextEffectRender => {
    // 방어적 판별 — 레지스트리 디스패치상 항상 countUp 지만 타입 안전 위해 확인.
    if (config.type !== 'countUp') {
      return { visibleText: fullText, cursor: false };
    }
    const value = countUpValue(
      config.from,
      config.to,
      frame,
      fps,
      config.durationInSeconds,
    );
    const grouped = formatGrouped(value);
    const visibleText = config.format
      ? config.format.replace('{n}', grouped)
      : grouped;
    return { visibleText, cursor: false };
  },
};
